import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  times,
  isEqual,
  uniqBy,
  orderBy,
  map,
  filter,
  get,
  size,
  differenceBy,
  findIndex,
} from "lodash";
import cx from "classnames";
import { nanoid } from "nanoid";
import { toast } from "react-toastify";
import {
  rectSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDndMonitor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";

import Summary from "../../components/actionPlan/summary";
import Stage from "../../components/actionPlan/stage";
import DeleteActionSlider from "../../components/actionPlan/sliders/deleteActionSlider";
import DeleteStageSlider from "../../components/actionPlan/sliders/deleteStageSlider";
import CompleteActionSlider from "../../components/actionPlan/sliders/completeActionSlider";
import ChangeActionSlider from "../../components/actionPlan/sliders/changeActionSlider";
import CascadeDatesSlider from "../../components/actionPlan/sliders/cascadeDatesSlider";
import { AddAssetSlider } from "../../components/actionPlan/sliders/addAssetsSlider";
import CascadingActionsBanner from "../../components/actionPlan/cascadingActionsBanner";
import Button from "src/components/button";
import Toast from "src/components/toast";
import Reveal from "src/components/animation";

import {
  DealRoomAction,
  DealRoomActionEditChanges,
  DealRoomActionEditSliderChanges,
  DealRoomRecipient,
  DealRoomStage,
  SaveDealRoomActionPayload,
} from "src/types/dealRoom";
import { Asset } from "src/types/assets";

import { dealRoomMapApi } from "src/services/dealRoom/actionPlan";
import {
  dealRoomMapInitialState,
  dealRoomMapReducer,
  getCascadeDateDayDifference,
} from "src/utils/dealRoom.helper";
import { DealRoomContext } from "../../components/context";
import { DealRoomActionPlanContext } from "../../components/actionPlan/context";

import styles from "./styles.module.scss";
const EmptyStages = "/images/emptyStages.svg";

const EmptyStagesScreen = ({
  onClick,
  canEdit = false,
}: {
  onClick: () => void;
  canEdit?: boolean;
}) => {
  return (
    <div className={styles.emptyScreenContainer}>
      <img src={EmptyStages} className={styles.icon} />
      <div className={styles.separator}></div>
      <div className={styles.emptyMessage}>
        <p className={styles.header}>Let's build your action plan!</p>
        <p className={styles.subtext}>
          Create stages and action items to keep your deal moving forward with
          clear next steps.
        </p>
        {canEdit && (
          <Button
            className={styles.action}
            onClick={onClick}
            icon={"paperflite-add"}
            variant={"primary"}
            text={"Create your first stage"}
          />
        )}
      </div>
    </div>
  );
};

const StagesLoading = () => {
  return (
    <div className={styles.stageContainer}>
      {times(3, (i) => (
        <div key={i} className={styles.stage}>
          <div className={styles.titlePlaceholder}></div>
          <div className={styles.statusPlaceholder}></div>
        </div>
      ))}
    </div>
  );
};

interface ReorderStagesProps {
  stages: DealRoomStage[];
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (updatedStagesInOrder: DealRoomStage[]) => void;
}

const ReorderMonitor: React.FC<ReorderStagesProps> = ({
  stages,
  onDragStart,
  onDragEnd,
}) => {
  useDndMonitor({
    onDragStart(event: DragStartEvent) {
      onDragStart(event);
    },

    onDragEnd(event: DragEndEvent) {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = stages.findIndex((action) => action.id === active.id);
      const newIndex = stages.findIndex((action) => action.id === over.id);

      const reorderedStages = [...stages];
      const [movedItem] = reorderedStages.splice(oldIndex, 1);
      reorderedStages.splice(newIndex, 0, movedItem);

      if (isEqual(stages, reorderedStages)) {
        return;
      }

      onDragEnd(reorderedStages);
    },
  });

  return null;
};

const ActionPlanPage = () => {
  const {
    id: dealRoomId,
    dealRoom,
    menuState,
    onMenuChange,
  } = useContext(DealRoomContext);

  const isEditing = menuState.primaryAction === "edit";
  const idRef = useRef(nanoid(6));

  const [state, dispatch] = useReducer(
    dealRoomMapReducer,
    dealRoomMapInitialState,
  );

  const [draggingStage, setDraggingStage] = useState<DealRoomStage | null>(
    null,
  );

  const [showCompleteActionSlider, setShowCompleteActionSlider] =
    useState(false);

  const [showDeleteStageSlider, setShowDeleteStageSlider] = useState(false);

  const [showChangeActionSlider, setShowChangeActionSlider] = useState(false);

  const [showDeleteActionSlider, setShowDeleteActionSlider] = useState(false);

  const {
    editMode,
    addAssetSlider,
    stages,
    actionsProgress,
    editingStage,
    creatingStage,
    assets,
    editingItem,
  } = state;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    if (dealRoomId) {
      fetchDealRoomStages(dealRoomId);
    }
  }, [dealRoomId]);

  useEffect(() => {
    if (get(menuState, "primaryAction", "") === "view") {
      dispatch({
        type: "INVALIDATE_ON_GLOBAL_EXIT",
      });
    }
  }, [menuState]);

  useEffect(() => {
    dispatch({
      type: "TOGGLE_EDIT_MODE",
      payload: {
        editMode: isEditing,
      },
    });
  }, [isEditing]);

  const fetchDealRoomActionsProgress = (dealRoomId: string) => {
    dealRoomMapApi
      .fetchActionsProgress(dealRoomId)
      .then((data) =>
        dispatch({
          type: "FETCHING_ACTIONS_PROGRESS_SUCCEEDED",
          payload: {
            progress: data,
          },
        }),
      )
      .catch((error) =>
        dispatch({
          type: "FETCHING_ACTIONS_PROGRESS_FAILED",
          payload: {
            error,
          },
        }),
      );
  };

  const fetchDealRoomStages = async (dealRoomId: string) => {
    if (!dealRoomId) {
      return;
    }

    dispatch({ type: "FETCH_STAGES" });
    dealRoomMapApi
      .fetchStages(dealRoomId)
      .then((data) =>
        dispatch({
          type: "FETCHING_STAGES_SUCCEEDED",
          payload: {
            stages: get(data, "stages", []),
          },
        }),
      )
      .catch((error) =>
        dispatch({ type: "FETCHING_STAGES_FAILED", payload: { error } }),
      );

    fetchDealRoomActionsProgress(dealRoomId);
  };

  const handleFetchRecipients = async (dealRoomId: string, page: number) => {
    dispatch({ type: "FETCH_RECIPIENTS_FOR_MAP" });

    dealRoomMapApi
      .fetchRecipientsForMap(dealRoomId, page)
      .then((data) =>
        dispatch({
          type: "FETCHING_RECIPIENTS_FOR_MAP_SUCCEEDED",
          payload: {
            recipients: data,
          },
        }),
      )
      .catch((error) => {
        dispatch({
          type: "FETCHING_RECIPIENTS_FOR_MAP_FAILED",
          payload: { error },
        });
      });
  };

  const handleFetchAssets = async (dealRoomId: string, page: number) => {
    dispatch({ type: "FETCH_ASSETS_FOR_MAP" });

    dealRoomMapApi
      .fetchAssetsForMap(dealRoomId, page)
      .then((data) =>
        dispatch({
          type: "FETCHING_ASSETS_FOR_MAP_SUCCEEDED",
          payload: {
            assets: data,
          },
        }),
      )
      .catch((error) => {
        dispatch({
          type: "FETCHING_ASSETS_FOR_MAP_FAILED",
          payload: { error },
        });
      });
  };

  const handleAddNewStage = () => {
    if (editingItem) {
      return;
    }

    onMenuChange({
      ...menuState,
      primaryAction: "edit",
    });
    dispatch({
      type: "TOGGLE_EDIT_MODE",
      payload: {
        editMode: true,
      },
    });
    dispatch({ type: "ADD_NEW_STAGE" });
  };

  const handleTitleChange = (newValue: string) => {
    dispatch({
      type: "UPDATE_NEW_STAGE",
      payload: {
        newStageTitle: newValue,
      },
    });
  };

  const handleCreateNewStage = () => {
    const stages = get(state, "stages.items");

    if (size(stages) === 0 || !dealRoomId) {
      return;
    }

    dispatch({ type: "CREATE_NEW_STAGE" });

    const newStageTitle = get(stages[size(stages) - 1], "title", "");

    if (size(newStageTitle) === 0) {
      return;
    }

    const payload = {
      name: newStageTitle,
    };

    dealRoomMapApi
      .createStage(dealRoomId, payload)
      .then((data) =>
        dispatch({
          type: "CREATING_NEW_STAGE_SUCCEEDED",
          payload: {
            newStage: data,
          },
        }),
      )
      .catch((error) =>
        dispatch({
          type: "CREATING_NEW_STAGE_FAILED",
          payload: {
            error,
          },
        }),
      );
  };

  const handleCancelCreateNewStage = () => {
    dispatch({ type: "INVALIDATE_NEW_STAGE" });
  };

  const handleToggleEditStage = (stageId: string) => {
    if (!stageId || !editMode) {
      return;
    }

    dispatch({
      type: "TOGGLE_EDIT_STAGE",
      payload: {
        stageId,
      },
    });
  };

  const handleCancelEditStage = () => {
    dispatch({ type: "INVALIDATE_EDIT_STAGE" });
  };

  const handleEditStage = (stageId: string, newTitle: string) => {
    if (!stageId) {
      return;
    }

    dispatch({ type: "EDIT_STAGE", payload: { stageId, newTitle } });
  };

  const handleUpdateStage = (stageId: string) => {
    const editingStage = get(state, "editingStage.item");
    const payload = {
      id: get(editingStage, "id", ""),
      name: get(editingStage, "title", ""),
    };

    if (!dealRoomId || !stageId || !payload) {
      return;
    }

    dispatch({ type: "UPDATE_STAGE" });

    dealRoomMapApi
      .updateStage(dealRoomId, payload)
      .then((data) => {
        dispatch({
          type: "UPDATING_STAGE_SUCCEEDED",
          payload: {
            modifiedStage: data,
          },
        });
      })
      .catch((error) => {
        dispatch({
          type: "UPDATING_STAGE_FAILED",
          payload: {
            error,
          },
        });
      });
  };

  const clickDeleteStage = (stageId: string) => {
    if (!dealRoomId || !stageId) {
      return;
    }

    dispatch({
      type: "TOGGLE_DELETE_STAGE",
      payload: {
        stageId,
      },
    });

    setShowDeleteStageSlider(true);
  };

  const handleDeleteStage = (stageId: string) => {
    if (!stageId || !dealRoomId) {
      return;
    }

    dispatch({ type: "DELETE_STAGE" });

    dealRoomMapApi
      .deleteStage(dealRoomId, stageId)
      .then((data) => {
        if (data === true) {
          dispatch({
            type: "DELETING_STAGE_SUCCEEDED",
            payload: {
              stageId,
            },
          });
        }
      })
      .catch((error) => {
        dispatch({
          type: "DELETING_STAGE_FAILED",
          payload: {
            error,
          },
        });
      })
      .finally(() => {
        fetchDealRoomActionsProgress(dealRoomId);
        handleInvalidateDeleteStage();
      });
  };

  const handleInvalidateDeleteStage = () => {
    setShowDeleteStageSlider(false);
    dispatch({ type: "INVALIDATE_DELETE_STAGE" });
  };

  const handleStageDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const stageItems = get(stages, "items", []);

    if (active.id) {
      const stageToDrag = stageItems.find((stage) => stage.id === active.id);
      if (stageToDrag) {
        setDraggingStage(stageToDrag);
      }
    }
  };

  const handleStageReorder = (updatedStagesInOrder: DealRoomStage[]) => {
    if (!dealRoomId || size(updatedStagesInOrder) === 0) {
      return;
    }

    dispatch({ type: "UPDATE_STAGE_ORDER", payload: updatedStagesInOrder });

    const payloadForStageReorder = {
      stageIds: map(updatedStagesInOrder, (stage) => stage.id),
    };

    dealRoomMapApi
      .reorderStage(dealRoomId, payloadForStageReorder)
      .then((data) =>
        dispatch({ type: "UPDATING_STAGE_ORDER_SUCCEEDED", payload: data }),
      )
      .catch((error) =>
        dispatch({
          type: "UPDATING_STAGE_ORDER_FAILED",
          payload: {
            error,
          },
        }),
      );
  };

  const handleFetchActions = (stageId: string, page: number) => {
    if (!dealRoomId || !stageId) {
      return;
    }

    dispatch({ type: "FETCH_ACTIONS_FOR_STAGE", payload: { stageId } });

    dealRoomMapApi
      .fetchActionsForStage(dealRoomId, stageId, page)
      .then((data) =>
        dispatch({
          type: "FETCHING_ACTIONS_FOR_STAGE_SUCCEEDED",
          payload: { stageId, response: data },
        }),
      )
      .catch((err) =>
        dispatch({
          type: "FETCHING_ACTIONS_FOR_STAGE_FAILED",
          payload: { stageId, error: err },
        }),
      );
  };

  const handleEditAction = (
    changes: DealRoomActionEditChanges,
    stageId: string,
    actionId: string,
    isNewAction: boolean,
  ) => {
    dispatch({
      type: "EDIT_ACTION",
      payload: {
        changes,
        stageId,
        actionId,
        isNewAction,
      },
    });
  };

  const handleCancelEditAction = (
    action: DealRoomAction,
    isNewAction: boolean,
  ) => {
    const stageId = get(action, "stage");
    if (!dealRoomId || !stageId) {
      return;
    }

    if (isNewAction) {
      dispatch({ type: "INVALIDATE_CREATE_NEW_ACTION", payload: stageId });
    } else {
      dispatch({ type: "INVALIDATE_EDIT_ACTION", payload: stageId });
    }
  };

  const handleSaveAction = (
    action: DealRoomAction,
    stageId: string,
    isNewAction: boolean,
  ) => {
    const actionId = get(action, "id");

    if (!dealRoomId || !stageId || !action) {
      return;
    }

    if (!isNewAction) {
      const actions = get(state, `actions[${stageId}].items`, []);
      const oldActionIndex = findIndex(
        actions,
        (action) => action.id === actionId,
      );

      if (oldActionIndex < 0) {
        return;
      }

      const oldAction = actions[oldActionIndex];
      const oldRecipients = get(
        oldAction,
        "recipients",
        [],
      ) as DealRoomRecipient[];
      const oldAssets = get(oldAction, "assets", []) as Asset[];

      const newAssets = get(action, "assets", []) as Asset[];
      const newlyAddedAssets = differenceBy(newAssets, oldAssets, "id");
      const removeAssets = differenceBy(oldAssets, newAssets, "id");

      const newRecipients =
        get(action, "recipients", []) || ([] as DealRoomRecipient[]);
      const newlyAddedRecipients = differenceBy(
        newRecipients,
        oldRecipients,
        "shareRecipient",
      );
      const removeRecipients = differenceBy(
        oldRecipients,
        newRecipients,
        "shareRecipient",
      );

      const oldActionItem = get(oldAction, "actionItem");
      const newActionItem = get(action, "actionItem");

      const oldDueDate = get(oldAction, "dueDate");
      const newDueDate = get(action, "dueDate");

      const actionForPayload: SaveDealRoomActionPayload = {
        id: get(action, "id"),
        stage: stageId,
      };

      if (
        !isEqual(
          map(orderBy(oldAssets, "id"), (asset) => get(asset, "id")),
          map(orderBy(newAssets, "id"), (asset) => get(asset, "id")),
        )
      ) {
        if (size(newlyAddedAssets) > 0) {
          actionForPayload.assets = map(
            newlyAddedAssets,
            (asset) => asset.id,
          );
        }

        if (size(removeAssets) > 0) {
          actionForPayload.removeAssets = map(
            removeAssets,
            (asset) => asset.id,
          );
        }
      }

      if (
        !isEqual(
          map(orderBy(oldRecipients, "shareRecipient"), (recipient) =>
            get(recipient, "shareRecipient"),
          ),
          map(orderBy(newRecipients, "shareRecipient"), (recipient) =>
            get(recipient, "shareRecipient"),
          ),
        )
      ) {
        if (size(newlyAddedRecipients) > 0) {
          actionForPayload.recipients = map(
            newlyAddedRecipients,
            (recipient) => recipient.shareRecipient,
          );
        }

        if (size(removeRecipients) > 0) {
          actionForPayload.removeRecipients = map(
            removeRecipients,
            (recipient) => recipient.shareRecipient,
          );
        }
      }

      if (oldActionItem !== newActionItem) {
        actionForPayload.actionItem = newActionItem;
      }

      if (newDueDate && size(newDueDate) > 0 && oldDueDate !== newDueDate) {
        actionForPayload.dueDate = newDueDate;
      }

      dispatch({
        type: "SAVE_EDIT_ACTION",
        payload: { stageId, actionId, updatedDate: actionForPayload?.dueDate ?? null  },
      });

      dealRoomMapApi
        .saveAction(dealRoomId, actionForPayload)
        .then((data) => {
          const { stageInfo, subsequentActionsCount, ...updatedAction } = data;

          dispatch({
            type: "SAVING_EDIT_ACTION_SUCCEEDED",
            payload: {
              stageId,
              actionId,
              updatedAction: updatedAction,
              updatedStageInfo: stageInfo,
              subsequentActionsCount,
              updatedDate: actionForPayload.dueDate ?? null,
            },
          });

          fetchDealRoomActionsProgress(dealRoomId);
          setShowChangeActionSlider(false);
        })
        .catch((error) =>
          dispatch({
            type: "SAVING_EDIT_ACTION_FAILED",
            payload: { stageId, actionId, error },
          }),
        );
    } else {
      const dueDate = get(action, "dueDate");
      const recipients = get(action, "recipients") ?? [];
      const assets = get(action, "assets") ?? [];

      const actionForPayload = {
        actionItem: get(action, "actionItem"),
        dueDate: dueDate && size(dueDate) > 0 ? dueDate : null,
        recipients: map(recipients, (recipient) => recipient.shareRecipient),
        assets: map(assets, (asset) => asset.id),
        stage: stageId,
      };

      dispatch({ type: "CREATE_NEW_ACTION", payload: { stageId } });

      dealRoomMapApi
        .createAction(dealRoomId, actionForPayload)
        .then((data) => {
          const { stageInfo } = data;
          dispatch({
            type: "CREATING_NEW_ACTION_SUCCEEDED",
            payload: { stageId, updatedStageInfo: stageInfo },
          });

          handleFetchActions(stageId, 0);
          fetchDealRoomActionsProgress(dealRoomId);
        })
        .catch((error) =>
          dispatch({
            type: "CREATING_NEW_ACTION_FAILED",
            payload: { stageId, error },
          }),
        );
    }
  };

  const handleReorderAction = (
    newActionsOrder: DealRoomAction[],
    stageId: string,
  ) => {
    if (!dealRoomId || size(newActionsOrder) === 0) {
      return;
    }

    const payloadForActionReorder = {
      stage: get(newActionsOrder, "[0].stage", ""),
      actionIds: map(newActionsOrder, "id"),
    };

    dispatch({
      type: "REORDERING_ACTIONS",
      payload: { stageId, updatedActionsInOrder: newActionsOrder },
    });

    dealRoomMapApi
      .reorderActions(dealRoomId, payloadForActionReorder)
      .then(() => {
        dispatch({
          type: "REORDERING_ACTIONS_SUCCEEDED",
          payload: { stageId },
        });
      })
      .catch((error) => {
        dispatch({
          type: "REORDERING_ACTIONS_FAILED",
          payload: { stageId, error },
        });
      });
  };

  const handleInvalidateDeleteAction = () => {
    setShowDeleteActionSlider(false);

    dispatch({ type: "INVALIDATE_DELETE_ACTION" });
  };

  const handleDeleteAction = (
    newActionsOrder: string[],
    stageId: string,
    deletedActionId: string,
  ) => {
    if (!dealRoomId) {
      return;
    }

    const payloadForActionReorder = {
      stage: stageId,
      actionIds: newActionsOrder,
      deleteActionIds: [deletedActionId],
    };

    dispatch({
      type: "DELETE_ACTION",
      payload: {
        stageId,
        actionId: deletedActionId,
      },
    });

    dealRoomMapApi
      .reorderActions(dealRoomId, payloadForActionReorder)
      .then((data) => {
        const completedActions = get(data, "completedActions", 0);
        const totalActions = get(data, "totalActions", 0);
        const totalAttachments = get(data, "totalAttachments", 0);
        const totalRecipients = get(data, "totalRecipients", 0);

        dispatch({
          type: "DELETING_ACTION_SUCCEEDED",
          payload: {
            stageId,
            actionId: deletedActionId,
            newActionsOrder,
            completedActions,
            totalActions,
            totalAttachments,
            totalRecipients,
          },
        });
        handleFetchActions(stageId, 0);
      })
      .catch((error) => {
        handleFetchActions(stageId, 0);
        dispatch({
          type: "DELETING_ACTION_FAILED",
          payload: {
            stageId,
            actionId: deletedActionId,
            error,
          },
        });
      })
      .finally(() => {
        handleInvalidateDeleteAction();
        fetchDealRoomActionsProgress(dealRoomId);
      });
  };

  const toggleEditAction = (
    stageId: string,
    actionId: string,
    completed: boolean,
  ) => {
    if (!stageId || !actionId || completed) {
      return;
    }

    dispatch({ type: "INITIATE_EDIT_ACTION", payload: { stageId, actionId } });
  };

  const handleSelectRecipient = (
    stageId: string,
    actionId: string,
    isNewAction: boolean,
    selectedRecipient: DealRoomRecipient,
    isAdded: boolean,
  ) => {
    if (!stageId || !actionId) {
      return;
    }

    let targetAction = isNewAction
      ? get(state, `actions[${stageId}].newAction`)
      : get(state, `actions[${stageId}].editAction`);

    if (!targetAction) {
      return;
    }

    const recipients = get(targetAction, "item.recipients", []);
    let changes = {};

    if (isAdded) {
      const modifiedRecipients = [...recipients];

      modifiedRecipients.push(selectedRecipient);

      changes = {
        recipients: modifiedRecipients,
      };
    } else {
      const modifiedRecipients = filter(
        recipients,
        (recipient) =>
          recipient.shareRecipient !== selectedRecipient.shareRecipient,
      );

      changes = {
        recipients: modifiedRecipients,
      };
    }

    dispatch({
      type: "EDIT_ACTION",
      payload: {
        stageId: stageId,
        actionId: actionId,
        changes,
        isNewAction,
      },
    });
  };

  const handleAddAssets = (
    stageId: string,
    actionId: string,
    isNewAction: boolean,
    selectedAssets: Asset[],
  ) => {
    if (!stageId || !actionId) {
      return;
    }

    let targetAction = isNewAction
      ? get(state, `actions[${stageId}].newAction`)
      : get(state, `actions[${stageId}].editAction`);

    if (!targetAction) {
      return;
    }

    let changes = {};

    const modifiedAssets = [...selectedAssets];

    uniqBy(modifiedAssets, "id");

    changes = {
      assets: modifiedAssets,
    };

    dispatch({
      type: "EDIT_ACTION",
      payload: {
        stageId: stageId,
        actionId: actionId,
        changes,
        isNewAction,
      },
    });
  };

  const clickCompleteAction = (
    stageId: string,
    actionId: string,
    completed: boolean,
  ) => {
    if (!dealRoomId) {
      return;
    }

    dispatch({
      type: "COMPLETE_ACTION",
      payload: {
        stageId,
        actionId,
        completed,
      },
    });

    setShowCompleteActionSlider(true);
  };

  const handleCompleteAction = (
    stageId: string,
    actionId: string,
    completed: boolean,
  ) => {
    if (!dealRoomId) {
      return;
    }

    dispatch({
      type: "COMPLETING_ACTION",
    });

    dealRoomMapApi
      .completeAction(dealRoomId, actionId, completed)
      .then((data) => {
        const { stageInfo, ...updatedAction } = data;
        dispatch({
          type: "COMPLETING_ACTION_SUCCEEDED",
          payload: {
            stageId,
            actionId,
            updatedAction,
            updatedStageInfo: stageInfo,
          },
        });
      })
      .catch((error) => {
        dispatch({
          type: "COMPLETING_ACTION_FAILED",
          payload: {
            stageId,
            actionId,
            error,
          },
        });
      })
      .finally(() => {
        fetchDealRoomActionsProgress(dealRoomId);
        handleInvalidateCompleteAction();
      });
  };

  const handleInvalidateCompleteAction = () => {
    setShowCompleteActionSlider(false);
    dispatch({ type: "INVALIDATE_COMPLETE_ACTION" });
  };

  const toggleAddAssetsSlider = (
    showSlider: boolean,
    stageId: string,
    actionId?: string,
    isNewAction?: boolean,
    existingAssets?: Asset[],
  ) => {
    if (!editMode) {
      return;
    }

    dispatch({
      type: "TOGGLE_ADD_ASSETS_SLIDER",
      payload: {
        show: showSlider,
        stageId: stageId ?? "",
        actionId: actionId ?? "",
        isNewAction: isNewAction ?? false,
        existingAssets: existingAssets ?? [],
      },
    });
  };

  const clickSaveAction = (
    action: DealRoomAction,
    stageId: string,
    changes: DealRoomActionEditSliderChanges,
    stakeholdersToBeNotified: DealRoomRecipient[],
  ) => {
    setShowChangeActionSlider(true);

    dispatch({
      type: "TOGGLE_SAVE_ACTION_SLIDER",
      payload: {
        action,
        stageId,
        changes,
        stakeholdersToBeNotified,
      },
    });
  };

  const handleInvalidateSaveActionSlider = () => {
    const sliderAction = get(state, "saveActionSlider.action") as DealRoomAction;
    const stageId = get(state, "saveActionSlider.stageId", "");

    setShowChangeActionSlider(false);
    dispatch({ type: "INVALIDATE_SAVE_ACTION_SLIDER" });

    if (sliderAction && stageId) {
      handleCancelEditAction(sliderAction, false);
    }
  };

  const clickDeleteAction = (action: DealRoomAction, stageId: string) => {
    setShowDeleteActionSlider(true);

    dispatch({
      type: "TOGGLE_DELETE_ACTION_SLIDER",
      payload: {
        action,
        stageId,
      },
    });
  };

  const handleStageAccordionToggle = (stageId: string) => {
    dispatch({
      type: "TOGGLE_STAGE_ACCORDION",
      payload: {
        stageId,
      },
    });
  };

  const cascading = state.cascadingActionItem;
  const cascadingTarget = cascading.targetAction;
  const cascadingUpdatedDate = cascading.updatedDate;
  const anchorActionId = cascading.anchorActionId;
  const subsequentActionsCount = cascading.subsequentActionsCount;

  const showCascadingActionsBanner =
    !!cascadingTarget &&
    !!cascadingUpdatedDate &&
    subsequentActionsCount > 0 &&
    !!cascadingTarget.dueDate;

  const dayDifference = showCascadingActionsBanner
    ? getCascadeDateDayDifference(
        cascadingUpdatedDate as string,
        cascadingTarget.dueDate as string,
      )
    : 0;

  const CascadeSuccessToastContent = ({dateDifference}: {dateDifference: number}) => {
    const absDays = Math.abs(dateDifference);
    const dayWord = absDays === 1 ? "day" : "days";
    const direction = dateDifference >= 0 ? "forward" : "backward";

    return (
      <p className={styles.successToast}>
        All subsequent dates were shifted {direction} by <span className={styles.highlight}>"{absDays} {dayWord}"</span>
      </p>
    )
  }

  const cascadeActions = useCallback(async () => {
    if (!dealRoomId || !cascadingTarget?.dueDate || !anchorActionId) {
      return;
    }

    dispatch({ type: "INITIATE_CASCADE_DATES" });

    try {
      const dateDifference = getCascadeDateDayDifference(
        cascadingUpdatedDate as string,
        cascadingTarget.dueDate as string,
      );

      const data = await dealRoomMapApi.cascadeDates(dealRoomId, {
        targetDueDate: cascadingTarget.dueDate as string,
        dateDifference,
        anchorActionId,
      });

      dispatch({
        type: "CASCADE_DATES_SUCCEEDED",
        payload: { updatedActions: data.updatedActions ?? [] },
      });

      toast(
        <Toast.Sucess
          message=''
          customContent={
            <CascadeSuccessToastContent dateDifference={dateDifference} />
          }
        />,
        { toastId: "cascade-dates-success" },
      );

      fetchDealRoomActionsProgress(dealRoomId);
    } catch (error) {
      dispatch({
        type: "CASCADE_DATES_FAILED",
        payload: { error },
      });

      toast(
        <Toast.Failure message="Something happened, please try again in a while." />,
        { toastId: "cascade-dates-failure" },
      );
    }
  }, [dealRoomId, cascadingTarget, cascadingUpdatedDate, anchorActionId, dispatch]);

  const closeCascadingActionsBanner = useCallback(() => {
    dispatch({ type: "INVALIDATE_CASCADING_ACTION_ITEM" });
  }, []);

  const openCascadeDatesSlider = useCallback(() => {
    dispatch({
      type: "TOGGLE_CASCADE_DATES_SLIDER",
      payload: { show: true },
    });
  }, []);

  const closeCascadeDatesSlider = useCallback(() => {
    dispatch({
      type: "TOGGLE_CASCADE_DATES_SLIDER",
      payload: { show: false },
    });
  }, []);

  const dealRoomActionPlanContextState = {
    state: state,
    handleTitleChange: handleTitleChange,
    handleCreateNewStage: handleCreateNewStage,
    handleCancelCreateNewStage: handleCancelCreateNewStage,
    handleDeleteStage: handleDeleteStage,
    handleFetchActions: handleFetchActions,
    handleEditAction: handleEditAction,
    handleSaveAction: handleSaveAction,
    handleReorderAction: handleReorderAction,
    toggleEditAction: toggleEditAction,
    handleToggleEditStage: handleToggleEditStage,
    handleCancelEditStage: handleCancelEditStage,
    handleEditStage: handleEditStage,
    handleUpdateStage: handleUpdateStage,
    handleCancelEditAction: handleCancelEditAction,
    handleSelectRecipient: handleSelectRecipient,
    handleFetchRecipients: handleFetchRecipients,
    clickCompleteAction: clickCompleteAction,
    toggleAddAssetsSlider: toggleAddAssetsSlider,
    handleFetchAssets: handleFetchAssets,
    handleCompleteAction: handleCompleteAction,
    clickDeleteStage: clickDeleteStage,
    handleInvalidateDeleteStage: handleInvalidateDeleteStage,
    clickSaveAction: clickSaveAction,
    clickDeleteAction: clickDeleteAction,
    handleStageAccordionToggle: handleStageAccordionToggle,
    showChangeActionSlider: showChangeActionSlider,
    handleInvalidateSaveActionSlider: handleInvalidateSaveActionSlider,
  };

  return (
    <DealRoomActionPlanContext.Provider value={dealRoomActionPlanContextState}>
      <div className={styles.pageContainer}>
        <h3 className={styles.pageDescription}>
          A shared action plan to track milestones and next steps together.
        </h3>
        <Reveal
          show={showCascadingActionsBanner}
          type="slide-up"
          config={{ distance: 100 }}
        >
          <CascadingActionsBanner
            dayDifference={dayDifference}
            noOfCascadingActions={subsequentActionsCount}
            actionItemName={get(
              cascadingTarget,
              "actionItem",
              "this action item",
            )}
            cascadeActions={openCascadeDatesSlider}
            closeBanner={closeCascadingActionsBanner}
            cascadeLoading={cascading.cascadeDates.loading}
          />
        </Reveal>
        <div className={styles.contentContainer}>
          {get(stages, "loading", false) ? (
            <StagesLoading />
          ) : size(stages.items) == 0 ? (
            <EmptyStagesScreen
              onClick={handleAddNewStage}
              canEdit={get(dealRoom, "canEdit", false)}
            />
          ) : (
            <>
              <Summary summaryDetails={get(actionsProgress, "data")} />
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToWindowEdges]}
              >
                <ReorderMonitor
                  stages={get(stages, "items", [])}
                  onDragStart={handleStageDragStart}
                  onDragEnd={handleStageReorder}
                />
                <SortableContext
                  id={idRef.current}
                  items={stages.items.map((stage) => stage?.id || "")}
                  strategy={rectSortingStrategy}
                >
                  <div
                    className={cx(styles.stageContainer, {
                      [styles.showCreateStage]:
                        editMode &&
                        editingStage.id === null &&
                        creatingStage.id === null,
                    })}
                  >
                    {stages.items.map((stage: DealRoomStage, index: number) => {
                      return (
                        <Stage
                          key={`stage-${index}`}
                          stage={
                            get(stage, "id") === editingStage.id
                              ? editingStage.item
                              : stage
                          }
                          editing={get(stage, "id") === editingStage.id}
                          creating={get(stage, "id") === creatingStage.id}
                          isDragOverlay={false}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {draggingStage && (
                    <Stage
                      stage={draggingStage}
                      editing={false}
                      creating={false}
                      isDragOverlay={true}
                    />
                  )}
                </DragOverlay>
              </DndContext>
              {editMode &&
                editingStage.id === null &&
                creatingStage.id === null && (
                  <div
                    className={cx(styles.createNewStageContainer, {
                      [styles.disabled]: !!editingItem,
                    })}
                    onClick={handleAddNewStage}
                  >
                    <i className={cx(styles.icon, "paperflite-add")}></i>
                    <p className={styles.text}>add stage</p>
                  </div>
                )}
            </>
          )}
        </div>

        <CompleteActionSlider
          show={showCompleteActionSlider}
          handleClose={handleInvalidateCompleteAction}
          handleFinish={handleCompleteAction}
        />
        <DeleteStageSlider
          show={showDeleteStageSlider}
          handleClose={handleInvalidateDeleteStage}
          handleFinish={handleDeleteStage}
        />
        <DeleteActionSlider
          show={showDeleteActionSlider}
          handleClose={handleInvalidateDeleteAction}
          handleFinish={handleDeleteAction}
        />
        <ChangeActionSlider
          show={showChangeActionSlider}
          handleClose={handleInvalidateSaveActionSlider}
          handleFinish={handleSaveAction}
        />
        <CascadeDatesSlider
          show={
            get(cascading, "showConfirmationSlider", false) &&
            showCascadingActionsBanner
          }
          handleClose={closeCascadeDatesSlider}
          handleFinish={cascadeActions}
        />
        <AddAssetSlider
          dealRoomId={dealRoomId ?? ""}
          show={
            get(addAssetSlider, "show", false) &&
            !!get(addAssetSlider, "stageId") &&
            !!get(addAssetSlider, "actionId")
          }
          onFinish={handleAddAssets}
          onClose={({ cancelled } = {}) => {
            const stageId = get(addAssetSlider, "stageId", "");
            const isNewAction = get(addAssetSlider, "isNewAction", false);

            toggleAddAssetsSlider(false, "", "");

            if (!cancelled || !stageId) {
              return;
            }

            // Opening attach calls toggleEditAction, which sets editingItem.
            // Cancel must clear it or the next attach on another action will wiggle.
            if (isNewAction) {
              dispatch({ type: "INVALIDATE_EDITING_ITEM" });
            } else {
              dispatch({ type: "INVALIDATE_EDIT_ACTION", payload: stageId });
            }
          }}
          assets={assets.items}
          fetchAssets={handleFetchAssets}
        />
      </div>
    </DealRoomActionPlanContext.Provider>
  );
};

export default ActionPlanPage;
